import React from "react";
import {
  AddressKeys,
  AddressStateType,
  AddressStatusType,
  AutoCompleteSuggestionType,
} from "core-lib/types/Address";
import useReverseGeoCode, {
  ReverseGeoCodeType,
} from "../../services/useReverseGeoCode";
import useAutoComplete from "./useAutoComplete";

type AutoCompleteWrapperTypes = {
  address: AddressStateType;
  suggestions: AutoCompleteSuggestionType[];
  query: string;
  fetch: (givenLocatoion?: { lat: number; lng: number }) => void;
  updateQuery: (args: string) => void;
  selectPlaceId: (data: AutoCompleteSuggestionType) => void;
  reset: () => void;
  suggestionAPiStatus: string;
};

export default function useAutoCompleteWithRGC({
  rgc = {
    enableRGCApi: true,
    enableFallbackRGCApi: false,
  },
  addressValidation = ["pincode"],
  serviceName
}: {
  rgc?: ReverseGeoCodeType;
  addressValidation?: AddressKeys[];
  serviceName ?: string
}): AutoCompleteWrapperTypes {
  const [address, setAddress] = React.useState<AddressStateType>({
    status: AddressStatusType.Default,
    data: null,
  });
  const placeLocalityRef = React.useRef<string | null>(null);

  const { address: rgcAddress, fetch: fetchRGC } = useReverseGeoCode({
    ...rgc,
    addressValidation,
  });
  const {
    address: placeDetailAddress,
    suggestions,
    query,
    updateQuery,
    selectPlaceId,
    reset,
    suggestionAPiStatus,
  } = useAutoComplete(serviceName);

  React.useEffect(() => {
    if (placeDetailAddress.status === AddressStatusType.Success) {
      const isValid = addressValidation.every(
        (el) => placeDetailAddress.data[el]
      ) && !!placeDetailAddress.data.city;
      if (isValid) {
        setAddress(placeDetailAddress);
      } else {
        placeLocalityRef.current = placeDetailAddress.data.locality;
        fetchRGC({
          lat: +placeDetailAddress.data.latitude,
          lng: +placeDetailAddress.data.longitude,
        });
      }
    } else if (placeDetailAddress.status === AddressStatusType.Failed) {
      setAddress(placeDetailAddress);
    }
  }, [placeDetailAddress]);

  React.useEffect(() => {
    if (rgcAddress.status !== AddressStatusType.Default) {
      const locality = placeLocalityRef.current;
      placeLocalityRef.current = null;
      setAddress({
        ...rgcAddress,
        data: rgcAddress.data
          ? { ...rgcAddress.data, locality: locality || rgcAddress.data.locality }
          : rgcAddress.data,
      });
    }
  }, [rgcAddress]);

  return {
    address,
    suggestions,
    query,
    fetch: fetchRGC,
    updateQuery,
    selectPlaceId,
    reset,
    suggestionAPiStatus,
  };
}
